<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import Loading from '@/components/Loading.vue'
import CarAppearance from '@/components/CarAppearance.vue'
import CrestAppearance from '@/components/CrestAppearance.vue'
import PageHeader from '@/components/PageHeader.vue'
import { Config } from '@/Config'
import { WHITE, paintKey } from '@/lib/paint'
import { useMainStore } from '@/stores/main'
import * as Language from '@/services/Language'
import * as Timezone from '@/services/Timezone'
import * as GameOptions from '@/services/GameOptions'
import * as Alerts from '@/services/Alerts'
// Notifications: the component renders nothing when the server does not have the feature.
import PushSettings from '@/push/PushSettings.vue'

const RACE_MIN_PLAYERS = 2;

/** The "automatic" choice of the timezone selector: the server then follows the browser's one. */
const AUTOMATIC = ''

const { t } = useI18n()

const store = useMainStore()

// Reactive data
const loading = ref(true)
const saving = ref(false)
const settings = ref({
  language: Language.current(),
  // The timezone the dates are written in (see services/Timezone): an IANA name, or AUTOMATIC to
  // follow the one the browser declares.
  timezone: Timezone.chosen() ?? AUTOMATIC,
  // In-game options. Which colours and liveries the account may pick is the server's answer and it
  // comes WITH the settings (GameOptions.current), which is why it is kept here rather than read
  // from the bootstrap: the page draws those lists and nothing else. The values themselves are on
  // the server too (user_settings). `paint` is what the car is painted with — the controls and the
  // preview are CarAppearance's, this page only carries the value.
  paint: { color: null, color2: null, color3: null, color4: null, livery: null },
  // The crest worn next to the name: its shape and the order its zones are painted in, by colour
  // index (1 = bodywork). It rides the launch link like the paint, so the game reads it from there.
  crest: { shape: 'solid', order: [1] },
  colors: [],      // the colours this account may pick (its slice of config/colors.php)
  anyColor: false, // whether it may pick any colour at all (color.rgb)
  liveries: [],    // the livery ids it may wear (config/appearance.php)
  crests: [],      // the crest shapes there are (config/crests.php)
  headToHead: false,
})

// The cars on offer (config/cars.php, from the bootstrap).
const offeredCars = computed(() => (store.cars ?? []).map((car) => car.id))

// The car the paint preview is drawn on: the one config/cars.php marks, the same for everybody and
// always the same one — so what two players see can be compared, and the preview does not change
// with the car an account happens to have driven last. A car with no livery catalogue would leave
// the livery picker empty, which is why the marked one is a car that has liveries.
const previewCar = computed(() => (store.cars ?? []).find((car) => car.preview)?.id ?? offeredCars.value[0] ?? '')

// The colours the chosen livery really paints, by index: the car's own file says how many there are,
// and CarAppearance owns that file (it draws from it). Null until it answers.
const paintPalette = ref(null)

// The colours a crest is drawn with: the paint's own, the same ones the pickers edit.
const crestPalette = computed(() => paintPalette.value === null ? null : paintPalette.value
  .map((index) => settings.value.paint[paintKey(index)] ?? WHITE))

const setPaintPalette = (indices) => (paintPalette.value = indices)

const route = useRoute()
const router = useRouter()

// The page is split in tabs: "main" is what it always had, "in-game" holds what the game itself
// applies, the notifications have one of their own (the component renders nothing when the server
// does not have the feature).
const TABS = ['main', 'game', 'notifications']

// The tab is in the address (?tab=notifications), so a link can land on the right one: it is what
// the notification emails point at.
const tabFromUrl = () => (TABS.includes(route.query.tab) ? route.query.tab : 'main')

const tab = ref(tabFromUrl())

// Keep the address in step with the tab. `replace` and not `push`: switching tabs is not a step of
// its own, and "main" is the default, so it stays out of the address.
watch(tab, (value) => {
  const query = { ...route.query }

  if (value === 'main') {
    delete query.tab
  } else {
    query.tab = value
  }

  router.replace({ query })
})

// A link opened while the page is already there still moves to its tab.
watch(() => route.query.tab, (value) => {
  const target = TABS.includes(value) ? value : 'main'

  if (target !== tab.value) {
    tab.value = target
  }
})

// The notifications hold their own state: the single save button of this page asks them to save
// through this reference.
const pushSection = ref(null)

// Languages offered by the site (see Config.languages).
const languageItems = Object.entries(Config.languages)
  .map(([value, title]) => ({ value, title }))

// The timezone the dates follow: automatic (the one the browser declares) or one of the server's
// list. The value is what user_settings.timezone holds, so AUTOMATIC is the empty choice = null.
const timezoneItems = computed(() => [
  { value: AUTOMATIC, title: t('settings.timezone.automatic', { timezone: Timezone.automatic() ?? '' }) },
  ...Timezone.available().map((timezone) => ({ value: timezone, title: timezone })),
])

// Methods
const loadSettings = async () => {
  loading.value = true
  try {
    // The language saved on the server (user_settings) is the user preference.
    settings.value.language = await Language.syncFromServer()
    // Same for the timezone: the server decides it, and applies it on the client on the way in.
    await Timezone.syncFromServer()
    settings.value.timezone = Timezone.chosen() ?? AUTOMATIC
    // The in-game options live on the server too: the game reads them from the launch link, so
    // this only shows the current choice.
    const gameOptions = await GameOptions.current()
    settings.value.paint = {
      color:  gameOptions.color,
      color2: gameOptions.color2,
      color3: gameOptions.color3,
      color4: gameOptions.color4,
      livery: gameOptions.livery,
    }
    settings.value.headToHead = gameOptions.headToHead
    settings.value.colors = gameOptions.colors
    settings.value.anyColor = gameOptions.anyColor
    settings.value.liveries = gameOptions.liveries
    settings.value.crest = gameOptions.crest
    settings.value.crests = gameOptions.crests
  } catch (error) {
    console.error('Error loading settings:', error)

  } finally {
    loading.value = false
  }
}

const saveSettings = async () => {
  saving.value = true
  try {
    // Save the preference on the server and apply it on the client.
    await Language.save(settings.value.language)
    await Timezone.save(settings.value.timezone)

    // In-game options: the game applies them from the next launch (they ride the signed link).
    if (settings.value.paint.color) {
      await GameOptions.save({
        ...settings.value.paint,
        crest: settings.value.crest,
        headToHead: settings.value.headToHead,
      })
    }

    // One button for the whole page: the notifications are saved by their own component, which
    // owns that part of the state (and is not rendered at all when the server has no push).
    const saved = pushSection.value ? await pushSection.value.save() : true

    if (!saved) {
      throw new Error('the notification preferences were not saved')
    }

    Alerts.success(t('settings.saved'))
  } catch (error) {
    console.error('Error saving settings:', error)
    Alerts.error(t('settings.saveError'))
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadSettings()
})
</script>

<template>
  <v-container class="settings-container">

    <!-- Loading state -->
    <Loading v-if="loading" :message="$t('settings.loading')" />

    <!-- Main content - only show when not loading -->
    <v-fade-transition>
      <div v-if="!loading">
        <!-- Header -->
        <PageHeader :title="$t('settings.title')" icon="mdi-cog" />

        <!-- Groups of settings. "main" is in front because it holds what the page always had; the
             in-game ones are what the game itself applies; the notifications come from src/push
             and answer only when the server has the feature. -->
        <v-tabs v-model="tab" color="primary" class="mb-4">
          <v-tab value="main" prepend-icon="mdi-cog-outline">{{ $t('settings.tabs.main') }}</v-tab>
          <v-tab value="game" prepend-icon="mdi-palette-outline">{{ $t('settings.tabs.game') }}</v-tab>
          <v-tab value="notifications" prepend-icon="mdi-bell-ring-outline">
            {{ $t('settings.tabs.notifications') }}
          </v-tab>
        </v-tabs>

        <v-row>
          <v-col cols="12" md="8">
            <v-tabs-window v-model="tab">
              <!-- Main: language, timezone and the account email -->
              <v-tabs-window-item value="main">
                <!-- Language Section -->
                <v-card elevation="2" class="mb-6">
                  <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-translate</v-icon>
                    {{ $t('settings.language.title') }}
                  </v-card-title>
                  <v-card-subtitle>
                    {{ $t('settings.language.subtitle') }}
                  </v-card-subtitle>

                  <v-card-text>
                    <v-select
                      v-model="settings.language"
                      :items="languageItems"
                      :label="$t('settings.language.select')"
                      variant="outlined"
                      :disabled="saving"
                    ></v-select>
                  </v-card-text>
                </v-card>

                <!-- Timezone Section -->
                <v-card elevation="2" class="mb-6">
                  <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-clock-time-four-outline</v-icon>
                    {{ $t('settings.timezone.title') }}
                  </v-card-title>
                  <v-card-subtitle>
                    {{ $t('settings.timezone.subtitle') }}
                  </v-card-subtitle>

                  <v-card-text>
                    <v-autocomplete
                      v-model="settings.timezone"
                      :items="timezoneItems"
                      :label="$t('settings.timezone.select')"
                      :no-data-text="$t('settings.timezone.noMatch')"
                      variant="outlined"
                      :disabled="saving"
                    ></v-autocomplete>
                  </v-card-text>
                </v-card>

                <!-- Email Section: the address is changed on its own page, it needs the password and
                     puts the account back to waiting for activation -->
                <v-card elevation="2">
                  <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-email-outline</v-icon>
                    {{ $t('settings.email.title') }}
                  </v-card-title>
                  <v-card-subtitle>
                    {{ $t('settings.email.subtitle') }}
                  </v-card-subtitle>

                  <v-card-text>
                    <v-btn
                      :to="{ name: 'change_email' }"
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-email-edit-outline"
                    >
                      {{ $t('settings.email.change') }}
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-tabs-window-item>

              <!-- In-game: what the game applies to the player. It reaches the game through the
                   signed launch link, so a change here takes effect from the next time the player
                   gets on track — nothing is pushed to a session already running. -->
              <v-tabs-window-item value="game">
                <v-card elevation="2">
                  <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-car-sports</v-icon>
                    {{ $t('settings.game.title') }}
                  </v-card-title>
                  <v-card-subtitle>
                    {{ $t('settings.game.subtitle') }}
                  </v-card-subtitle>

                  <v-card-text>
                    <CarAppearance
                      v-model="settings.paint"
                      :car="previewCar"
                      :cars="offeredCars"
                      :colors="settings.colors"
                      :any-color="settings.anyColor"
                      :liveries="settings.liveries"
                      :disabled="saving"
                      @palette="setPaintPalette"
                    />

                    <template v-if="RACE_MIN_PLAYERS > 2">
                      <v-divider class="my-5" />
                      <div class="text-subtitle-2 mb-1">{{ $t('settings.game.headToHead.title') }}</div>
                      <p class="text-body-2 text-medium-emphasis mb-2">
                        {{ $t('settings.game.headToHead.subtitle') }}
                      </p>
                      <v-radio-group
                        v-model="settings.headToHead"
                        inline
                        hide-details
                        :disabled="saving"
                      >
                        <v-radio :label="$t('settings.game.headToHead.enable')" :value="true" />
                        <v-radio :label="$t('settings.game.headToHead.disable')" :value="false" />
                      </v-radio-group>
                    </template>
                  </v-card-text>
                </v-card>

                <!-- The crest next to the name: its shape, tinted with the very colours the livery
                     above is painted with. -->
                <v-card elevation="2" class="mt-6">
                  <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-shield-half-full</v-icon>
                    {{ $t('settings.game.crest.title') }}
                  </v-card-title>
                  <v-card-subtitle>
                    {{ $t('settings.game.crest.subtitle') }}
                  </v-card-subtitle>

                  <v-card-text>
                    <CrestAppearance
                      v-model="settings.crest"
                      :colors="crestPalette"
                      :shapes="settings.crests"
                      :name="store.user?.name ?? ''"
                      :disabled="saving"
                    />
                  </v-card-text>
                </v-card>
              </v-tabs-window-item>

              <!-- Notifications (src/push). It stays mounted while the other tab is shown: what it
                   holds is what the page's save button sends. -->
              <v-tabs-window-item value="notifications" eager>
                <PushSettings ref="pushSection" />
              </v-tabs-window-item>
            </v-tabs-window>
          </v-col>

          <!-- One button for the whole page (the change of the email is a page of its own) -->
          <v-col cols="12" md="4">
            <v-card elevation="1">
              <v-card-text>
                <v-btn
                  block
                  color="primary"
                  size="large"
                  prepend-icon="mdi-content-save"
                  :loading="saving"
                  @click="saveSettings"
                >
                  {{ $t('settings.saveChanges') }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-fade-transition>
  </v-container>
</template>

